import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import Container from '@material-ui/core/Container';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import RefreshIcon from './RefreshIcon.svg'

const useStyles = makeStyles(theme => ({
    root: {
        [theme.breakpoints.up('md')]: {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
    },
    wrapper: {
        background: theme.colors.hoverBlue,
        border: '1px solid #05C3DE',
        borderRadius: 4,
        boxSizing: 'border-box',
        height: 40,
        position: process.env.REACT_APP_ENV === 'local' ? 'absolute' : 'none',
    },
    card: {
        minHeight: 264,
        paddingRight: 0,
        borderRadius: '0px',
        boxShadow:
            '0px 0px 8px rgba(59, 59, 59, 0.12), 0px 6px 4px rgba(59, 59, 59, 0.05), 0px 3px 3px rgba(59, 59, 59, 0.05)',
        '&:hover': {
            boxShadow:
                '0px 2px 15px rgba(59, 59, 59, 0.1), 0px 10px 15px rgba(0, 0, 0, 0.12), 0px 4px 3px rgba(0, 0, 0, 0.04)',
        },
        [theme.breakpoints.only('md')]: {
            minHeight: 272,
            height: 1
        }
    },
    details: {
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
    },
    content: {
        padding: '32px 32px 24px',
        '&:last-child': {
            paddingBottom: 24,
        },
        [theme.breakpoints.down('sm')]: {
            padding: 24
        }
    },
    cardContentText: {
        display: 'flex',
        justifyContent: 'center',
        color: theme.colors.darkGray,
    },
    cardHeaderText: {
        fontSize: theme.fontVars.xlarge,
        lineHeight: '32px',
        fontFamily: theme.fontFamily.light,
        color: theme.colors.darkGray,
    },
    cardDataText: {
        fontSize: theme.fontVars.medium,
        lineHeight: '22px',
        fontFamily: theme.fontFamily.light,
        paddingTop: theme.gutterFPS * 3,
        color: theme.colors.darkGray,
    },
    cardDataSubText: {
        fontSize: theme.fontVars.medium,
        lineHeight: '22px',
        fontFamily: theme.fontFamily.light,
        paddingTop: theme.gutterFPS * 4,
        color: theme.colors.darkGray,
    },
    cardButton: {
        boxShadow: 'none',
        background: theme.colors.white,
        border: '1px solid #086EA1',
        padding: '8px 16px',
        '&:hover': {
            backgroundColor: theme.colors.hoverBlue,
            color: theme.colors.white,
            border: '1px solid #043954',
        },
        marginTop: theme.gutterFPS * 8,
        marginBottom: theme.gutterFPS * 2,
        fontSize: theme.fontVars.medium,
        lineHeight: '16px',
        maxHeight: '32px',
        fontFamily: theme.fontFamily.reg,
        color: theme.colors.interactiveBlue,
        textTransform: 'none',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            marginTop: theme.gutterFPS * 2,
        },
    },
    cardLink: {
        color: theme.palette.interactiveBlue
    },
    cardButtonNoOutline: {
        boxShadow: 'none',
        background: theme.colors.white,
        marginBottom: theme.gutterFPS * 2,
        fontSize: theme.fontVars.medium,
        lineHeight: '16px',
        maxHeight: '32px',
        fontFamily: theme.fontFamily.reg,
        color: theme.colors.interactiveBlue,
        textTransform: 'none',
        '& .arrow-icon': {
            paddingLeft: theme.gutterFPS * 2,
        },
        '&:hover': {
            backgroundColor: 'transparent',
            color: theme.colors.hoverBlue,
            border: 'none',
        },
        '&:active': {
            boxShadow: 'none',
        },
        padding: '16px 0 0 0',
    },
    cardButtonContainer: {
        padding: '12px',
        flexGrow: '1',
        alignContent: 'flex-end',
        [theme.breakpoints.down('sm')]: {
            padding: '32px 12px 12px',
        }
    },
    cardImage: {
        marginBottom: theme.gutterFPS * 4,
        maxHeight: '72px'
    },
    backgroundImage: {
        backgroundImage: `url(${RefreshIcon})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right top',
        minHeight: 76
    }
}));

const CardButton =
    ({
        className,
        id,
        onClick,
        cardButtonText,
        noOutline,
        buttonIcon,
        href,
        target
    }) => {
        const [hover, setHover] = React.useState(null);
        const onEnter = () => setHover(true);
        const onLeave = () => setHover(false);
        return (
            <div>
                {!href && (
                    <Button
                        id={id}
                        size="large"
                        className={className}
                        onClick={onClick}
                        disableRipple={noOutline}
                        color="primary"
                        variant="contained"
                        onMouseEnter={onEnter}
                        onMouseLeave={onLeave}>
                        {cardButtonText}{' '}
                        {buttonIcon && React.cloneElement(buttonIcon, { filled: !!hover })}
                    </Button>
                )}
                {href && (
                    <Button
                        id={id}
                        size="large"
                        className={className}
                        color="primary"
                        variant="contained"
                        href={href}
                        disableRipple={noOutline}
                        target={target || "_blank"}
                        onMouseEnter={onEnter}
                        onMouseLeave={onLeave}>
                        {cardButtonText}{' '}
                        {buttonIcon && React.cloneElement(buttonIcon, { filled: !!hover })}
                    </Button>
                )}
            </div>
        );
};

export default function CardAction({
    buttonId,
    contentText,
    cardDataSubText,
    headerText,
    onClick,
    cardId,
    cardButtonText,
    cardLinkText,
    href,
    target,
    cardIcon,
    maxWidth = 'xl',
    noOutline,
    buttonIcon,
    trademarked
}) {
    const classes = useStyles();
    const getButtonClass = noOutline ? classes.cardButtonNoOutline : classes.cardButton;
    return (
            <div>
                <Container maxWidth={maxWidth} className={classes.root} id={cardId}>
                    <Card className={classes.card}>
                        <div className={classes.details}>
                            <div className={classes.cardContentText}>
                                <CardContent className={classes.content}>
                                    <div style={{ minHeight: '130px' }}>
                                        <Typography component="h5" variant="h5">
                                            <div className={classes.cardHeaderText} id={`${cardId}-title`}>
                                                {headerText}{trademarked ? <sup>®</sup> : ''}
                                            </div>
                                            <div className={classes.cardDataText} id={`${cardId}-main-content`}>
                                                {contentText}
                                            </div>
                                            {cardDataSubText && (
                                                <div>
                                                    <Hidden xsDown>
                                                        <div className={classes.backgroundImage}
                                                             style={{ backgroundImage: `url(${cardIcon})` }}
                                                             id={`${cardId}-secondary-content`}>
                                                            <div className={classes.cardDataSubText}>
                                                                {cardDataSubText}
                                                            </div>
                                                        </div>
                                                    </Hidden>
                                                    <Hidden smUp>
                                                        <div className={classes.cardDataSubText} id={`${cardId}-secondary-content`}> {cardDataSubText}</div>
                                                    </Hidden>
                                                </div>
                                            )}
                                        </Typography>
                                    </div>
                                    <Grid container spacing={3} className={classes.cardButtonContainer}>
                                        <Grid item xs={12} sm={8} style={{ display: 'flex', alignSelf: 'flex-end' }}>
                                            {cardLinkText && (
                                                <Link
                                                    color="inherit"
                                                    href={href}
                                                    target={target || "_blank"}
                                                    underline="none"
                                                    className={classes.cardLink}
                                                >
                                                    {cardLinkText}
                                                </Link>
                                            )}
                                            {cardButtonText && (
                                                <CardButton
                                                    className={getButtonClass}
                                                    id={buttonId}
                                                    onClick={onClick}
                                                    href={href}
                                                    target={target}
                                                    noOutline={noOutline}
                                                    cardButtonText={cardButtonText}
                                                    buttonIcon={buttonIcon}
                                                />
                                            )}
                                        </Grid>
                                        {!cardDataSubText && (
                                            <Hidden xsDown>
                                                <Grid item sm={4} style={{ alignSelf: 'center' }}>
                                                    <Box>
                                                        <div>
                                                            <img src={cardIcon} alt="add-co-client" style={{ float: 'right', maxWidth: '72px', maxHeight: '72px' }} />
                                                        </div>
                                                    </Box>
                                                </Grid>
                                            </Hidden>
                                        )}
                                    </Grid>
                                </CardContent>
                            </div>
                        </div>
                    </Card>
                </Container>
            </div>
    );
}
